/* eslint-disable react/no-unescaped-entities */
import { Camera, Barcode, ClockIcon, Pencil, Sparkles, ArrowRight, Check, Bookmark, BookOpen, Brain, MapPin, Send, TestTube, FileText, TrendingUp } from "lucide-react";

/* ---------------- Sub-panels ---------------- */

function DashboardPanel() {
  const macros = [
    { label: "Calories", val: 1700, target: 2200, color: "#7C5CFC", suffix: "kcal" },
    { label: "Protein", val: 166, target: 180, color: "#00E5A0", suffix: "g" },
    { label: "Carbs", val: 84, target: 220, color: "#9CA3AF", suffix: "g" },
    { label: "Fat", val: 74, target: 75, color: "#F59E0B", suffix: "g" },
  ];
  const tools = [
    { Icon: Camera, label: "Photo", sub: "AI plate scan", tag: "Free", tagClass: "text-brand-free border-brand-free/30" },
    { Icon: Barcode, label: "Barcode", sub: "Nutrition labels", tag: "Smart", tagClass: "text-brand-smart border-brand-smart/30" },
    { Icon: ClockIcon, label: "Log Meal", sub: "Recent meals", tag: "Free", tagClass: "text-brand-free border-brand-free/30" },
    { Icon: Pencil, label: "Manual", sub: "Restaurant-aware", tag: "Smart", tagClass: "text-brand-smart border-brand-smart/30" },
  ];
  return (
    <div data-testid="showcase-dashboard" className="glass-strong rounded-3xl p-6 sm:p-7">
      <div className="flex items-center justify-between mb-5">
        <div className="text-[11px] uppercase tracking-[0.22em] text-white/55 font-semibold">Dashboard · Live</div>
        <div className="flex items-center gap-1.5 text-[11px] text-brand-free font-semibold">
          <span className="h-1.5 w-1.5 rounded-full bg-brand-free animate-glow-pulse" /> 84% Complete
        </div>
      </div>

      {/* MIQ row */}
      <div className="flex items-center gap-5 mb-6">
        <div className="relative w-[110px] h-[110px] shrink-0">
          <div className="absolute inset-0 rounded-full conic-ring" />
          <div className="absolute inset-[5px] rounded-full bg-[#0A0A0A] flex flex-col items-center justify-center">
            <div className="font-mono text-3xl font-bold leading-none">74</div>
            <div className="mt-0.5 text-[9px] uppercase tracking-[0.2em] text-white/50">/ 100</div>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-semibold">Metabolic Intelligence</div>
          <div className="font-display font-bold text-lg mt-0.5">Today's Macros</div>
          <div className="text-xs text-brand-free mt-0.5">On track · Good</div>
        </div>
      </div>

      {/* Macro rings */}
      <div className="grid grid-cols-4 gap-2">
        {macros.map((m) => {
          const pct = Math.min(100, Math.round((m.val / m.target) * 100));
          const r = 22;
          const c = 2 * Math.PI * r;
          const dash = (pct / 100) * c;
          return (
            <div key={m.label} className="rounded-2xl bg-white/[0.025] border border-white/5 p-3 flex flex-col items-center">
              <svg width="56" height="56" viewBox="0 0 56 56" className="-rotate-90">
                <circle cx="28" cy="28" r={r} stroke="rgba(255,255,255,0.06)" strokeWidth="4" fill="none" />
                <circle cx="28" cy="28" r={r} stroke={m.color} strokeWidth="4" fill="none" strokeLinecap="round" strokeDasharray={`${dash} ${c}`} />
              </svg>
              <div className="font-mono text-sm font-bold mt-1">{m.val}</div>
              <div className="text-[9px] text-white/45">{m.label}</div>
              <div className="text-[9px] text-white/35 font-mono">/{m.target}{m.suffix}</div>
            </div>
          );
        })}
      </div>

      {/* Quick log tools */}
      <div className="mt-5 grid grid-cols-4 gap-2">
        {tools.map((t) => (
          <div key={t.label} className="rounded-xl border border-white/8 bg-white/[0.025] p-2.5 flex flex-col items-center text-center gap-1">
            <t.Icon className="w-4 h-4 text-white/80" />
            <div className="text-[11px] font-semibold leading-tight">{t.label}</div>
            <div className={`text-[8px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full border font-bold ${t.tagClass}`}>{t.tag}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SmartMealsPanel() {
  return (
    <div data-testid="showcase-smartmeals" className="glass-strong rounded-3xl p-6 sm:p-7">
      <div className="flex items-center justify-between mb-5">
        <div className="font-display font-bold text-base">SmartMeals</div>
        <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-accent/15 border border-brand-accent/30 text-[10px] uppercase tracking-[0.18em] text-brand-accent font-bold">
          <Sparkles className="w-3 h-3" /> AI
        </div>
      </div>

      <div className="rounded-2xl bg-white/[0.025] border border-white/5 p-4">
        <div className="flex items-center justify-between">
          <div className="text-xs text-white/65 font-semibold">Remaining Targets</div>
          <div className="relative w-12 h-12">
            <svg width="48" height="48" viewBox="0 0 48 48" className="-rotate-90">
              <circle cx="24" cy="24" r="18" stroke="rgba(255,255,255,0.08)" strokeWidth="4" fill="none" />
              <circle cx="24" cy="24" r="18" stroke="#F59E0B" strokeWidth="4" fill="none" strokeLinecap="round" strokeDasharray={`${(91/100) * 2 * Math.PI * 18} 999`} />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center font-mono text-[11px] font-bold">91%</div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2 mt-3">
          {[
            ["200kcal", "Cal", "#F59E0B"],
            ["15g", "Protein", "#00E5A0"],
            ["10g", "Carbs", "#3B82F6"],
            ["20g", "Fat", "#F59E0B"],
          ].map(([v, l, c]) => (
            <div key={l} className="rounded-xl bg-white/[0.02] border border-white/5 p-2 text-center">
              <div className="font-mono text-sm font-bold" style={{ color: c }}>{v}</div>
              <div className="text-[9px] text-white/45">{l}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 rounded-2xl overflow-hidden border border-white/5">
        <div className="h-24 bg-gradient-to-br from-[#3a2a14] via-[#1f2b1e] to-[#0a1414] flex items-end p-3 relative">
          <span className="text-[10px] absolute top-3 right-3 px-2 py-0.5 rounded-full bg-brand-free/15 border border-brand-free/40 text-brand-free font-bold uppercase tracking-[0.16em]">
            94% Perfect Fit
          </span>
          <div className="text-2xl">🥗</div>
        </div>
        <div className="p-4">
          <div className="font-display font-bold text-base">High-Protein Burrito Bowl</div>
          <div className="text-[11px] text-white/50 mt-0.5">15 min · Mexican</div>
          <div className="grid grid-cols-4 gap-1.5 mt-3">
            {[
              ["580", "kcal", "#F59E0B"],
              ["48g", "P", "#A855F7"],
              ["62g", "C", "#00E5A0"],
              ["14g", "F", "#F59E0B"],
            ].map(([v, l, c]) => (
              <div key={l} className="rounded-lg bg-white/[0.03] py-1.5 text-center">
                <div className="font-mono text-xs font-bold" style={{ color: c }}>{v}</div>
                <div className="text-[8px] text-white/45 uppercase">{l}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/70">High Protein</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/70">Gluten-Free</span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className="pill bg-brand-accent text-white text-[11px] font-semibold py-2 text-center flex items-center justify-center gap-1">
              <Check className="w-3 h-3" /> Log Meal
            </div>
            <div className="pill border border-brand-free/30 text-brand-free text-[11px] font-semibold py-2 text-center flex items-center justify-center gap-1">
              <BookOpen className="w-3 h-3" /> Recipe
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RecipeGenPanel() {
  const ingredients = ["Chicken", "Salmon", "Eggs", "Broccoli", "Sweet Potato", "Quinoa", "Avocado", "Cottage Cheese", "Tuna"];
  return (
    <div data-testid="showcase-recipe" className="glass-strong rounded-3xl p-6 sm:p-7">
      <div className="flex items-center justify-between mb-5">
        <div className="font-display font-bold text-base">Recipe Generator</div>
        <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-accent/15 border border-brand-accent/30 text-[10px] uppercase tracking-[0.18em] text-brand-accent font-bold">
          <Sparkles className="w-3 h-3" /> AI
        </div>
      </div>

      <div className="rounded-2xl border border-brand-accent/30 bg-brand-accent/5 p-4">
        <div className="text-[11px] text-white/65 font-semibold mb-2">🧪 What do you have?</div>
        <div className="rounded-lg border border-white/8 bg-black/30 px-3 py-2.5 text-xs text-white/35">
          Type ingredients or describe what you want…
        </div>
        <div className="mt-3 text-[10px] uppercase tracking-[0.18em] text-white/45 font-semibold">Or tap from common ingredients</div>
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {ingredients.map((ing) => (
            <span
              key={ing}
              className={`text-[10px] px-2.5 py-1 rounded-full border ${
                ing === "Cottage Cheese"
                  ? "bg-brand-accent text-white border-brand-accent"
                  : "bg-white/[0.03] text-white/70 border-white/10"
              }`}
            >
              {ing}
            </span>
          ))}
        </div>
        <div className="mt-3 text-[10px] text-brand-accent font-semibold">1 ingredient selected</div>
        <div className="mt-2.5 pill bg-brand-accent text-white text-xs font-semibold py-2.5 text-center">
          Generate AI Recipe
        </div>
      </div>

      <div className="mt-4 text-[10px] uppercase tracking-[0.22em] text-white/45 font-bold">3 Recipes Generated</div>
      <div className="mt-2.5 rounded-2xl bg-white/[0.025] border border-white/5 p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-sm">High-Protein Chicken Bowl</div>
            <div className="text-[10px] text-white/50 mt-1">Grilled chicken · brown rice · broccoli · sesame drizzle</div>
            <div className="mt-2 flex items-center gap-3 text-[10px] text-white/45">
              <span>⏱ 25 min</span><span>· Easy</span><span>· 1 serving</span>
            </div>
          </div>
          <Bookmark className="w-4 h-4 text-white/40 shrink-0" />
        </div>
        <div className="grid grid-cols-4 gap-1.5 mt-3">
          {[
            ["580", "kcal", "#F59E0B"],
            ["48g", "P", "#A855F7"],
            ["62g", "C", "#00E5A0"],
            ["14g", "F", "#F59E0B"],
          ].map(([v, l, c]) => (
            <div key={l} className="rounded-lg bg-white/[0.03] py-1.5 text-center">
              <div className="font-mono text-xs font-bold" style={{ color: c }}>{v}</div>
              <div className="text-[8px] text-white/45 uppercase">{l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SmartPicksMicrosPanel() {
  const picks = [
    { a: "Greek Yogurt", b: "Skyr", note: "+8g protein, −4g sugar", color: "bg-brand-accent" },
    { a: "White Rice", b: "Cauliflower Rice", note: "+9g fiber, −38g carbs", color: "bg-brand-free" },
    { a: "Butter", b: "Avocado Oil", note: "Better lipid profile", color: "bg-brand-smart" },
    { a: "Granola Bar", b: "Beef Jerky", note: "+12g protein, −15g sugar", color: "bg-brand-genius" },
  ];
  const micros = [
    ["Vit D", 32, "#F59E0B"],
    ["Vit A", 67, "#EF4444"],
    ["Magnesium", 70, "#A855F7"],
    ["Zinc", 73, "#94A3B8"],
    ["B12", 75, "#3B82F6"],
    ["Omega-3", 50, "#22D3EE"],
    ["Calcium", 62, "#E5E7EB"],
    ["Potassium", 80, "#F59E0B"],
  ];
  return (
    <div data-testid="showcase-smartpicks" className="glass-strong rounded-3xl p-6 sm:p-7">
      <div className="grid sm:grid-cols-2 gap-6">
        {/* Smart Picks */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-bold text-base">Smart Picks</div>
            <Brain className="w-4 h-4 text-brand-genius" />
          </div>
          <div className="text-[10px] text-white/45 mb-3 uppercase tracking-[0.18em] font-bold">Today's swaps</div>
          <div className="space-y-2">
            {picks.map((p, i) => (
              <div key={i} className="rounded-xl bg-white/[0.025] border border-white/5 p-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className={`h-2 w-2 rounded-full shrink-0 ${p.color}`} />
                  <div className="text-xs font-semibold text-white truncate">{p.a}</div>
                  <ArrowRight className="w-3 h-3 text-white/40 shrink-0" />
                  <div className="text-xs font-semibold text-white truncate">{p.b}</div>
                </div>
                <div className="text-[10px] text-white/50">{p.note}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Micronutrients */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-bold text-base">Micronutrients</div>
            <div className="text-[10px] text-white/45 font-mono">10 tracked</div>
          </div>
          <div className="text-[10px] text-white/45 mb-3 uppercase tracking-[0.18em] font-bold">Today's intake</div>
          <div className="space-y-2">
            {micros.map(([label, pct, color]) => (
              <div key={label}>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[11px] text-white/80 font-medium">{label}</div>
                  <div className="font-mono text-[10px] font-bold" style={{ color }}>{pct}%</div>
                </div>
                <div className="h-1 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full" style={{ width: `${pct}%`, backgroundColor: color }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Section ---------------- */

function RestaurantAwarePanel() {
  return (
    <div data-testid="showcase-restaurant" className="glass-strong rounded-3xl p-6 sm:p-8 lg:col-span-2 relative overflow-hidden">
      <div className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full conic-ring opacity-10 blur-3xl" />
      <div className="grid lg:grid-cols-[1.05fr_1.4fr] gap-8 lg:gap-12 items-stretch relative">
        {/* Left: pitch */}
        <div className="flex flex-col">
          <div className="inline-flex items-center gap-1.5 self-start px-2.5 py-1 rounded-full bg-brand-accent/15 border border-brand-accent/30 text-[10px] uppercase tracking-[0.2em] text-brand-accent font-bold mb-5">
            <MapPin className="w-3 h-3" /> Restaurant-Aware
          </div>
          <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tight leading-[1.05]">
            <span className="text-gradient-hero">"What should I eat</span>
            <br />
            at Chipotle?"
          </h3>
          <p className="mt-4 text-white/65 text-sm leading-relaxed font-light">
            SmartPlaite's Health Intelligence is the only AI that reads your{" "}
            <span className="text-white font-medium">live remaining macros</span> AND knows the actual menu
            at 500K+ restaurants — Chipotle, Sweetgreen, Chick-fil-A, Cava, your local sushi spot.
            Ask in plain English. Get the exact order that fits your goal.
          </p>
          <ul className="mt-5 space-y-2.5">
            {[
              "Menu-aware AI for 500K+ restaurants",
              "Reads your live macros + MIQ goal",
              "Returns the exact order, modifiers, and macro hit",
              "Auto-logs the meal once you confirm",
            ].map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-sm text-white/80">
                <Check className="w-4 h-4 text-brand-free mt-0.5 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Right: chat mock */}
        <div className="rounded-2xl border border-white/8 bg-[#080808] overflow-hidden flex flex-col">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5 bg-white/[0.02]">
            <Brain className="w-4 h-4 text-brand-accent" />
            <div className="font-display font-semibold text-xs">Health Intelligence</div>
            <div className="ml-auto flex items-center gap-1.5 text-[10px] text-brand-free font-semibold">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-free animate-glow-pulse" /> Live
            </div>
          </div>

          <div className="flex-1 p-4 space-y-3 text-sm">
            {/* User msg */}
            <div className="flex justify-end">
              <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-brand-accent text-white px-3.5 py-2.5">
                I'm at Chipotle. Based on my remaining macros, what should I order?
              </div>
            </div>

            {/* Context chip */}
            <div className="flex items-center gap-2 text-[10px] text-white/45 font-mono uppercase tracking-[0.14em]">
              <span className="h-px flex-1 bg-white/5" />
              Remaining today · 580 kcal · 42g P · 38g C · 18g F
              <span className="h-px flex-1 bg-white/5" />
            </div>

            {/* AI msg */}
            <div className="flex">
              <div className="max-w-[92%] rounded-2xl rounded-tl-sm bg-white/[0.04] border border-white/6 px-4 py-3 space-y-3">
                <div className="text-white/90 leading-relaxed">
                  Order this. <span className="font-semibold text-white">94% Perfect Fit:</span>
                </div>
                <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 font-bold mb-2">
                    Chicken Bowl
                  </div>
                  <ul className="space-y-1 text-xs text-white/85">
                    <li className="flex items-start gap-2"><span className="text-brand-free">•</span> Double chicken (no rice swap to extra fajita veggies)</li>
                    <li className="flex items-start gap-2"><span className="text-brand-free">•</span> Black beans (½ portion)</li>
                    <li className="flex items-start gap-2"><span className="text-brand-free">•</span> Fresh tomato salsa</li>
                    <li className="flex items-start gap-2"><span className="text-brand-free">•</span> Romaine + cheese</li>
                    <li className="flex items-start gap-2"><span className="text-white/40">○</span> <span className="text-white/55">Skip: sour cream, chips, dressing</span></li>
                  </ul>
                  <div className="mt-3 grid grid-cols-4 gap-1.5">
                    {[
                      ["540", "kcal", "#F59E0B"],
                      ["52g", "P", "#A855F7"],
                      ["28g", "C", "#3B82F6"],
                      ["18g", "F", "#F59E0B"],
                    ].map(([v, l, c]) => (
                      <div key={l} className="rounded-lg bg-black/40 py-1.5 text-center">
                        <div className="font-mono text-[11px] font-bold" style={{ color: c }}>{v}</div>
                        <div className="text-[8px] text-white/45 uppercase tracking-[0.12em]">{l}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-free/10 border border-brand-free/30 text-brand-free font-semibold">
                    <Sparkles className="w-3 h-3" /> MIQ +3
                  </span>
                  <span className="text-white/55">Lean protein · high fiber · low added sugar</span>
                </div>
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <div className="pill bg-brand-accent text-white text-[11px] font-semibold py-2 text-center flex items-center justify-center gap-1.5">
                    <Check className="w-3 h-3" /> Log this meal
                  </div>
                  <div className="pill border border-white/15 text-white/80 text-[11px] font-semibold py-2 text-center">
                    Try another
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Input bar */}
          <div className="border-t border-white/5 p-3 flex items-center gap-2 bg-white/[0.015]">
            <div className="flex-1 rounded-full bg-black/40 border border-white/8 px-3.5 py-2 text-xs text-white/35">
              Ask anything about your meals, training, recovery…
            </div>
            <div className="h-9 w-9 rounded-full bg-brand-accent flex items-center justify-center shrink-0">
              <Send className="w-3.5 h-3.5 text-white" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Section ---------------- */

function BloodworkPanel() {
  const markers = [
    { label: "LDL", value: "95 mg/dL", status: "Favorable", color: "#00E5A0", up: true },
    { label: "HDL", value: "62 mg/dL", status: "Optimal", color: "#00E5A0", up: true },
    { label: "Triglycerides", value: "88 mg/dL", status: "Favorable", color: "#00E5A0", up: false },
    { label: "HbA1c", value: "5.2%", status: "Optimal", color: "#00E5A0", up: false },
    { label: "hsCRP", value: "0.8 mg/L", status: "Low inflammation", color: "#3B82F6", up: false },
    { label: "Vitamin D", value: "42 ng/mL", status: "In range", color: "#3B82F6", up: true },
    { label: "Ferritin", value: "118 ng/mL", status: "Optimal", color: "#00E5A0", up: false },
    { label: "Testosterone", value: "612 ng/dL", status: "Strong", color: "#A855F7", up: true },
  ];

  return (
    <div data-testid="showcase-bloodwork" className="glass-strong rounded-3xl p-6 sm:p-8 lg:col-span-2 relative overflow-hidden">
      <div className="pointer-events-none absolute -top-24 -left-24 w-80 h-80 rounded-full bg-brand-genius opacity-[0.10] blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 w-80 h-80 rounded-full conic-ring opacity-10 blur-3xl" />

      <div className="grid lg:grid-cols-[1.05fr_1.4fr] gap-8 lg:gap-12 items-stretch relative">
        {/* Left: pitch */}
        <div className="flex flex-col">
          <div className="inline-flex items-center gap-1.5 self-start px-2.5 py-1 rounded-full bg-brand-genius/15 border border-brand-genius/30 text-[10px] uppercase tracking-[0.2em] text-brand-genius font-bold mb-5">
            <TestTube className="w-3 h-3" /> Labs-Aware
          </div>
          <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tight leading-[1.05]">
            <span className="text-gradient-hero">"What does my bloodwork</span>
            <br />
            say about my plan?"
          </h3>
          <p className="mt-4 text-white/65 text-sm leading-relaxed font-light">
            SmartPlaite is the only AI calorie app that reads your actual lab results — LDL, HbA1c,
            vitamin D, testosterone, hsCRP, ferritin, and 9 more — and rewrites your plan around them.
            Snap the PDF your doctor handed you. Get a Smart Coach insight that connects your labs to
            your food, sleep, and training. MIQ knows you better the second your blood draw lands.
          </p>
          <ul className="mt-5 space-y-2.5">
            {[
              "Multi-page PDF + photo OCR (parallel GPT-4o vision)",
              "15+ markers grouped by category — lipids, glucose, inflammation, hormones, vitamins",
              "\"Why this matters\" tooltip on every flagged result",
              "MIQ Confidence reveal — see your score climb the moment your labs save",
            ].map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-sm text-white/80">
                <Check className="w-4 h-4 text-brand-genius mt-0.5 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <a
            href="https://app.smartplaite.com/signup?plan=genius_monthly&trial=7"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="bloodwork-cta"
            className="pill mt-7 self-start inline-flex items-center gap-2 bg-brand-genius text-white px-5 py-3 text-sm font-semibold hover:bg-brand-genius/90"
          >
            Try it on Genius
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        {/* Right: phone-frame lab scan mock */}
        <div className="relative min-w-0 w-full">
          <div className="rounded-[28px] border border-white/10 bg-[#080808] overflow-hidden shadow-[0_20px_60px_rgba(168,85,247,0.20)] w-full">
            {/* Header bar */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5 bg-white/[0.02]">
              <TestTube className="w-4 h-4 text-brand-genius" />
              <div className="font-display font-semibold text-xs">Bloodwork Scanner</div>
              <div className="ml-auto flex items-center gap-1.5 text-[10px] text-brand-genius font-semibold">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-genius animate-glow-pulse" /> Reading
              </div>
            </div>

            {/* PDF preview row */}
            <div className="px-4 py-3 border-b border-white/5 flex items-center gap-3">
              <div className="w-10 h-12 rounded-md bg-gradient-to-br from-white/15 to-white/[0.02] border border-white/10 flex items-center justify-center shrink-0">
                <FileText className="w-4 h-4 text-white/65" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-white truncate">Quest_LabResults_2026-05-28.pdf</div>
                <div className="text-[10px] text-white/45 mt-0.5">2 pages · 18 markers detected · 2s</div>
              </div>
              <div className="font-mono text-[10px] text-brand-free font-bold whitespace-nowrap">+15% MIQ Conf.</div>
            </div>

            {/* MIQ Confidence reveal */}
            <div className="px-4 py-4 border-b border-white/5 bg-gradient-to-r from-brand-genius/[0.07] to-transparent">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-bold">MIQ Confidence</div>
                <div className="text-[10px] text-brand-free font-semibold inline-flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> +15%
                </div>
              </div>
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-brand-smart via-brand-accent to-brand-free" style={{ width: "92%" }} />
              </div>
              <div className="mt-1.5 flex items-baseline gap-1">
                <span className="font-mono text-2xl font-bold leading-none">92</span>
                <span className="text-[10px] text-white/45">/ 100</span>
              </div>
            </div>

            {/* Markers grid */}
            <div className="p-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-bold mb-3">Latest draw · 8 of 18 shown</div>
              <div className="grid grid-cols-2 gap-2">
                {markers.map((m) => (
                  <div key={m.label} className="rounded-xl bg-white/[0.025] border border-white/5 p-2.5 min-w-0">
                    <div className="flex items-center justify-between gap-1">
                      <div className="text-[10px] text-white/55 font-medium truncate">{m.label}</div>
                      <span
                        className="h-1.5 w-1.5 rounded-full shrink-0"
                        style={{ backgroundColor: m.color, boxShadow: `0 0 8px ${m.color}` }}
                      />
                    </div>
                    <div className="font-mono text-sm font-bold mt-1.5 break-words leading-tight">{m.value}</div>
                    <div className="text-[9px] mt-0.5 font-semibold truncate" style={{ color: m.color }}>{m.status}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Smart Coach insight */}
            <div className="px-4 pb-4">
              <div className="rounded-2xl border border-brand-accent/20 bg-brand-accent/[0.05] p-3.5">
                <div className="flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-brand-accent mt-0.5 shrink-0" />
                  <div className="text-[12px] text-white/85 leading-relaxed min-w-0 break-words">
                    <span className="font-semibold text-white">Smart Coach:</span> Your LDL dropped 18 pts and hsCRP is sub-1 — your plan is working. Stay on the current macro split, and keep omega-3 above 2g/day to lock it in.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProductShowcase() {
  return (
    <section
      id="inside"
      data-testid="showcase-section"
      className="relative py-28 md:py-36"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.24em] text-brand-accent font-bold mb-5">Inside the intelligence</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
            Not another tracker. <span className="text-gradient-hero">An entire brain.</span>
          </h2>
          <p className="mt-6 text-white/65 text-base sm:text-lg leading-relaxed font-light">
            Four ways to log a meal. AI recipes from your kitchen. 10+ micronutrients. Daily check-ins,
            fasting mode, smart swaps, weekly briefs — plus a restaurant-aware AI that tells you what
            to order at Chipotle. All running on one personalized MIQ engine.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-2 gap-6">
          <DashboardPanel />
          <SmartMealsPanel />
          <RecipeGenPanel />
          <SmartPicksMicrosPanel />
          <RestaurantAwarePanel />
          <BloodworkPanel />
        </div>
      </div>
    </section>
  );
}
